import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getApiUrl } from '../config/api';
import './ResetPassword.css';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  useEffect(() => {
    // Redirect to login after 3 seconds on success
    if (success) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  const validatePasswordStrength = (password) => {
    return {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\x5B\x5D{};':"\\|,.<>/?]/.test(password)
    };
  };

  const handlePasswordChange = (password) => {
    setNewPassword(password);
    setPasswordValidation(validatePasswordStrength(password));
  };

  const validateForm = () => {
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return false;
    }

    const validation = validatePasswordStrength(newPassword);
    const allValid = Object.values(validation).every(v => v);

    if (!allValid) {
      setError('Password does not meet all requirements');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(getApiUrl('/api/auth/reset-password'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-container">
      <motion.div
        className="reset-password-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="reset-password-header">
          <div className="icon-container">
            <div className="jpcc-seal-container">
              <img
                src={`${process.env.PUBLIC_URL}/JPCC_seal_trans_white.png`}
                alt="Jefferson Parish Clerk of Court Seal"
                className="jpcc-seal"
              />
            </div>
          </div>
          <h1 className="reset-password-title">Reset Your Password</h1>
          <p className="reset-password-subtitle">
            Jefferson Parish Clerk of Court
          </p>
        </div>

        <div className="reset-password-form-wrapper">
          {success ? (
            <motion.div
              className="success-message"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <p>
                <strong>Password reset successful!</strong>
              </p>
              <p>
                Your password has been updated. You will be redirected to the login page in a few seconds.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="reset-password-form">
              <div className="reset-password-instructions">
                <p>Please enter your new password below.</p>
              </div>

              {error && (
                <motion.div
                  className="error-message"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                >
                  {error}
                </motion.div>
              )}

              <div className="input-group">
                <input
                  type="password"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => {
                    handlePasswordChange(e.target.value);
                    setError('');
                  }}
                  placeholder="New Password"
                  className="form-input"
                  disabled={loading}
                  autoFocus
                />
              </div>

              {newPassword && (
                <motion.div
                  className="password-requirements"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <p className="requirements-title">Password must contain:</p>
                  <ul className="requirements-list">
                    <li className={passwordValidation.length ? 'valid' : 'invalid'}>
                      {passwordValidation.length ? '✓' : '○'} At least 12 characters
                    </li>
                    <li className={passwordValidation.uppercase ? 'valid' : 'invalid'}>
                      {passwordValidation.uppercase ? '✓' : '○'} One uppercase letter (A-Z)
                    </li>
                    <li className={passwordValidation.lowercase ? 'valid' : 'invalid'}>
                      {passwordValidation.lowercase ? '✓' : '○'} One lowercase letter (a-z)
                    </li>
                    <li className={passwordValidation.number ? 'valid' : 'invalid'}>
                      {passwordValidation.number ? '✓' : '○'} One number (0-9)
                    </li>
                    <li className={passwordValidation.special ? 'valid' : 'invalid'}>
                      {passwordValidation.special ? '✓' : '○'} One special character (!@#$%^&*)
                    </li>
                  </ul>
                </motion.div>
              )}

              <div className="input-group">
                <input
                  type="password"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError('');
                  }}
                  placeholder="Confirm New Password"
                  className="form-input"
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="loader"></div>
                    <span>Resetting Password...</span>
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>

              <div className="reset-password-footer">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => navigate('/')}
                  disabled={loading}
                >
                  Back to Login
                </button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
